/**
 * Supabase通信の期限
 * 会場Wi-Fiが混雑して応答が返らない通信を打ち切り、画面の送信中ロックが解けないまま止まらないようにする。
 */

/**
 * Supabaseへの1回の通信を打ち切るまでの時間（ミリ秒）
 * 来場者の通信はanonロールで、DB側の文の実行時間はSupabase既定の上限（anonは3秒）で止まる。
 * 残りの12秒を混雑した回線での送受信に充て、来場者が待ちきれず再読み込みする前に失敗を案内する。
 */
export const SUPABASE_REQUEST_TIMEOUT_MS = 15000;

/**
 * 期限切れで通信を打ち切ったことを表すエラーを作る
 * @param {number} timeoutMs - 期限（ミリ秒）
 * @returns {DOMException} TimeoutErrorという名前のエラー
 */
const createTimeoutError = (timeoutMs: number): DOMException =>
  new DOMException(`通信が${timeoutMs / 1000}秒以内に完了しなかったため中断しました。`, 'TimeoutError');

/**
 * 期限を過ぎた通信を中断するfetchを作る
 * 呼び出し元のsignalで中断された場合は、期限を待たずにその理由で失敗する。
 * @param {typeof fetch} baseFetch - 実際に通信するfetch
 * @param {number} timeoutMs - 期限（ミリ秒）
 * @returns {typeof fetch} 期限付きのfetch
 */
export const createTimeoutFetch = (baseFetch: typeof fetch, timeoutMs: number): typeof fetch => (
  input,
  init: RequestInit = {},
) => {
  /** 期限切れ・呼び出し元の中断をまとめて通信へ伝える */
  const controller = new AbortController();
  /** 呼び出し元のsignal */
  const callerSignal = init.signal ?? null;

  if (callerSignal?.aborted) {
    return Promise.reject(callerSignal.reason);
  }

  /**
   * 呼び出し元の中断を通信へ伝える
   * @returns {void}
   */
  const forwardCallerAbort = (): void => controller.abort(callerSignal?.reason);
  callerSignal?.addEventListener('abort', forwardCallerAbort, { once: true });

  // 応答のヘッダーを受け取った後も止めない。PostgRESTは同じsignalで本文を読むため、本文の受信が止まった場合も期限で中断できる。
  // 読み終えた後の中断は何も起こさない。
  /** 期限のタイマー */
  const timeoutId = setTimeout(() => controller.abort(createTimeoutError(timeoutMs)), timeoutMs);

  return new Promise<Response>((resolve, reject) => {
    /**
     * 中断されたら、signalに従わないfetchでも待ち続けずに失敗させる
     * @returns {void}
     */
    const rejectOnAbort = (): void => reject(controller.signal.reason);
    controller.signal.addEventListener('abort', rejectOnAbort, { once: true });

    baseFetch(input, { ...init, signal: controller.signal }).then(resolve, (error: unknown) => {
      clearTimeout(timeoutId);
      callerSignal?.removeEventListener('abort', forwardCallerAbort);
      reject(controller.signal.aborted ? controller.signal.reason : error);
    });
  });
};
